import { getBaseUrl } from '@hopin-team/dashboard-constants';

import type { ExportableModule } from '../features/top-bar/export';
import { httpClient } from './http-client';

export const getHost = () => getBaseUrl();

type AdminMonolithRequest = {
  organizationExternalId: string;
  path: string;
  query?: string;
  method?: 'get' | 'post';
  responseType?: 'blob';
};

const buildAdminMonolithUrl = ({
  organizationExternalId,
  path,
  query,
}: Pick<AdminMonolithRequest, 'organizationExternalId' | 'path' | 'query'>) => {
  const params = new URLSearchParams(query);

  params.append('organization_external_id', organizationExternalId);

  return `${getHost()}${path}?${params.toString()}`;
};

// Authorization is intentionally omitted here — the shared httpClient default
// (set by AuthenticationProvider and kept fresh by the 401 interceptor) is the
// single source of truth, matching how all non-admin requests work.
const requestAdminMonolith = async <T>({
  organizationExternalId,
  path,
  query,
  method = 'get',
  responseType,
}: AdminMonolithRequest): Promise<T> => {
  const response = await httpClient.request<T>({
    method,
    url: buildAdminMonolithUrl({
      organizationExternalId,
      path,
      query,
    }),
    responseType,
  });

  return response.data;
};

type AnalyticsReportModule = Exclude<ExportableModule, 'admin'>;

export const fetchEvents = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/events?${query}`,
  );
  return response.data;
};

export const fetchAggregatedEvents = async () => {
  const response = await httpClient.get(`/organizers/analytics/event_series`);
  return response.data;
};

const MODULE_TO_ENDPOINT: Record<AnalyticsReportModule, string> = {
  events: 'events/report',
  registrants: 'registrants/contact_report',
  videos: 'content_hub/videos_report',
  series: 'event_series_events/report',
} as const;
export const generateReport = async (
  module: AnalyticsReportModule,
  query: string,
) => {
  const endpoint = MODULE_TO_ENDPOINT[module];

  const response = await httpClient.get(
    `/organizers/analytics/${endpoint}?${query}`,
    {
      responseType: 'blob',
    },
  );

  return response.data;
};

export const fetchRegistrants = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/registrants?${query}`,
  );
  return response.data;
};

export const fetchEventsAttended = async (query: string) => {
  try {
    const response = await httpClient.get(
      `/organizers/analytics/events_attended_by_registrant?${query}&page=1&perPage=500`,
    );
    return response.data.eventsAttended;
  } catch (error) {
    console.error('Error registrants:', error);
  }
};

export const fetchTotalRegistrantsOverTime = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/total_registrants_over_time?${query}`,
  );
  return response.data;
};

export const fetchTotalTurnoutOverTime = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/total_turnout_over_time?${query}`,
  );
  return response.data;
};

export const fetchTotalEngagementOverTime = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/total_engagement_over_time?${query}`,
  );
  return response.data;
};

export const fetchTotals = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/totals?${query}`,
  );
  return response.data;
};

export const fetchTotalPageViewsOverTime = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/content_hub/total_page_views_over_time?${query}`,
  );
  return response.data;
};

export const fetchMostViewedHubVideos = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/content_hub/most_watched_videos?${query}`,
  );
  return response.data;
};

export const fetchVideos = async (query: string) => {
  const response = await httpClient.get(
    `/organizers/analytics/content_hub/watched_videos?${query}`,
  );
  return response.data;
};

export const fetchAdminEventUtilization = async (
  organizationExternalId: string,
  query: string,
) => {
  return requestAdminMonolith({
    organizationExternalId,
    query,
    path: '/organisers/analytics_api/admin/event_stats',
  });
};

export const fetchAdminLicenseConsumption = async (
  organizationExternalId: string,
) => {
  return requestAdminMonolith({
    organizationExternalId,
    path: '/organisers/analytics_api/admin/license_consumption',
  });
};

export const exportAdminAnalyticsReport = async (
  organizationExternalId: string,
  query: string,
) => {
  return requestAdminMonolith<Blob>({
    organizationExternalId,
    query,
    method: 'get',
    path: '/organisers/analytics_api/admin/export_admin_analytics',
    responseType: 'blob',
  });
};

export const fetchAdminEventsByOrganizer = async (
  organizationExternalId: string,
  query: string,
) => {
  return requestAdminMonolith({
    organizationExternalId,
    query,
    path: '/organisers/analytics_api/admin/events_by_organiser',
  });
};
